import {inject} from "@angular/core";
import {
  CanActivateFn,
  Router,
  UrlTree
} from "@angular/router";
import {Auth} from "@angular/fire/auth";
import {
  from,
  Observable,
  of
} from "rxjs";
import {
  catchError,
  map,
  switchMap,
  take
} from "rxjs/operators";

import {EhrClaims} from "../_interfaces/ehr-claims.interface";
import {AuthenticationService} from "../_services/authentication.service";

/**
 * Guard for the Office verification-code route.
 *
 * The verification route belongs to the local login transaction in this
 * browser tab. A global officeSessionId claim is not sufficient evidence that
 * this browser completed Office verification.
 */
export const verifyGuard: CanActivateFn = (
  _route,
  state
): Observable<boolean | UrlTree> => {
  const auth = inject(Auth);
  const router = inject(Router);
  const authService =
    inject(AuthenticationService);

  const toLogin = (): UrlTree =>
    router.createUrlTree(
      ["/login"],
      {
        queryParams: {
          redirectURL: state.url
        }
      }
    );

  const toSecure = (): UrlTree =>
    router.createUrlTree(
      ["/secure"]
    );

  return from(
    auth.authStateReady()
  ).pipe(
    take(1),

    switchMap(() => {
      const user =
        auth.currentUser;

      if (!user) {
        return of<boolean | UrlTree>(
          toLogin()
        );
      }

      /*
       * This browser tab successfully requested an Office verification code.
       *
       * The marker is route-flow state only. It does not grant Office access.
       * The verifyCodeForOffice API remains responsible for validating the
       * submitted code and creating the final Office session.
       */
      if (
        authService
          .hasPendingOfficeVerification()
      ) {
        return of<boolean | UrlTree>(
          true
        );
      }

      /*
       * No local verification transaction exists.
       *
       * Load fresh claims only to distinguish an already established Office
       * session in this browser from an invalid or foreign-browser claim.
       */
      return from(
        user.getIdTokenResult(true)
      ).pipe(
        map(tokenResult => {
          const claims =
            (
              tokenResult.claims ?? {}
            ) as EhrClaims;

          /*
           * The current browser already has a final matching Office session.
           * It should not remain on the verification route.
           */
          if (
            !authService
              .isOfficeMfaPending(claims) &&
            authService
              .hasMatchingOfficeSessionBinding(
                claims
              )
          ) {
            return toSecure();
          }

          /*
           * A global Office claim without the matching local browser binding
           * belongs to another browser session and must not be adopted here.
           */
          return toLogin();
        })
      );
    }),

    catchError(() => {
      return of(
        toLogin()
      );
    })
  );
};
